<template>
  <div class="user-page" dir="rtl">

    <!-- ── Page container ── -->
    <div class="user-page__container">

      <!-- H1 -->
      <h1 class="page-title">החשבון שלי</h1>

      <!-- ── Two-column layout ── -->
      <div class="account-layout">

         <!-- ── Sidebar ── -->
        <aside class="account-sidebar" aria-label="ניווט חשבון">
          <div class="sidebar-card">

            <!-- User mini card -->
            <div class="user-mini-card">
              <div class="user-mini-card__avatar" aria-hidden="true">
                {{ avatarInitial }}
              </div>
              <div class="user-mini-card__info">
                <span class="user-mini-card__name">{{ user.display_name || user.username }}</span>
                <span class="user-mini-card__email">{{ user.username }}</span>
                <span class="user-mini-card__badge" :class="'user-mini-card__badge--' + subscriptionTypeKey" aria-label="סוג מנוי">
                  {{ subscriptionTypeLabel }}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>
                </span>
              </div>
            </div>

            <!-- Divider -->
            <div class="sidebar-divider" role="separator"></div>

            <!-- Nav list -->
            <!-- Desktop: vertical nav list. Mobile (<=900px): wrapped pill row
                 showing every section at once — it used to be a Splide swiper,
                 so sections past the second pill needed a drag to reach. -->
            <nav aria-label="סעיפי חשבון" class="sidebar-nav-wrap">
                <ul class="sidebar-nav" role="list">
                  <li>
                    <button
                      type="button"
                      class="sidebar-nav__item"
                      :class="{ 'sidebar-nav__item--active': activeSection === 'profile' }"
                      @click="switchSection('profile')"
                      :aria-current="activeSection === 'profile' ? 'page' : undefined"
                    >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                     פרופיל
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      class="sidebar-nav__item"
                      :class="{ 'sidebar-nav__item--active': activeSection === 'billing' }"
                      @click="switchSection('billing')"
                      :aria-current="activeSection === 'billing' ? 'page' : undefined"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                      מנוי וחיוב
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      class="sidebar-nav__item"
                      :class="{ 'sidebar-nav__item--active': activeSection === 'password' }"
                      @click="switchSection('password')"
                      :aria-current="activeSection === 'password' ? 'page' : undefined"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      שינוי סיסמה
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      class="sidebar-nav__item"
                      :class="{ 'sidebar-nav__item--active': activeSection === 'privacy' }"
                      @click="switchSection('privacy')"
                      :aria-current="activeSection === 'privacy' ? 'page' : undefined"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      פרטיות
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      class="sidebar-nav__item sidebar-nav__item--logout"
                      @click="confirmLogout"
                      aria-label="התנתק מהחשבון"
                    > התנתק
                    <svg class="sidebar-nav__logout-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        </button>
                  </li>
                </ul>
            </nav>

          </div>
        </aside>

        <!-- ── Main panel ── -->
        <main class="account-main" role="main">

          <!-- PROFILE SECTION -->
          <section v-if="activeSection === 'profile'" class="panel-card" aria-label="פרטים אישיים">
            <div class="panel-card__header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              <h2 class="panel-card__title">פרטים אישיים</h2>
            </div>

            <div class="form-field">
              <label class="form-label" for="display-name">שם תצוגה</label>
              <input
                id="display-name"
                type="text"
                class="form-input"
                v-model="user.display_name"
                :aria-label="labels.accountOwner"
              />
            </div>

            <div class="form-field">
              <label class="form-label" for="email-field">אימייל</label>
              <input
                id="email-field"
                type="text"
                class="form-input form-input--disabled"
                :value="user.username"
                disabled
                :aria-label="labels.email"
              />
            </div>

            <div class="panel-card__footer">
              <button type="button" class="btn-save" @click="save_details">שמור שינויים</button>
            </div>
          </section>

          <!-- CHANGE PASSWORD SECTION (nested under profile, shown via sidebar) -->
          <section v-if="activeSection === 'password'" class="panel-card" aria-label="שינוי סיסמה">
            <div class="panel-card__header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <h2 class="panel-card__title">שינוי סיסמה</h2>
            </div>

            <div class="form-field">
              <label class="form-label" for="new-pass">סיסמה חדשה</label>
              <div class="pass-input-wrap">
                <input
                  id="new-pass"
                  :type="showNewPass ? 'text' : 'password'"
                  class="form-input pass-input"
                  v-model="new_password"
                  autocomplete="off"
                  placeholder="לפחות 6 תווים"
                />
                <button type="button" class="pass-eye" @click="showNewPass = !showNewPass" :aria-label="showNewPass ? 'הסתר סיסמה' : 'הצג סיסמה'">
                  <svg v-if="!showNewPass" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </div>

            <div class="form-field">
              <label class="form-label" for="confirm-pass">אימות סיסמה חדשה</label>
              <div class="pass-input-wrap">
                <input
                  id="confirm-pass"
                  :type="showConfirmPass ? 'text' : 'password'"
                  class="form-input pass-input"
                  v-model="confirm_password"
                  autocomplete="off"
                  placeholder="חזור על הסיסמה החדשה"
                />
                <button type="button" class="pass-eye" @click="showConfirmPass = !showConfirmPass" :aria-label="showConfirmPass ? 'הסתר סיסמה' : 'הצג סיסמה'">
                  <svg v-if="!showConfirmPass" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </div>

            <div v-if="pass_error" class="pass-error" role="alert">{{ pass_error }}</div>

            <div class="panel-card__footer">
              <button type="button" class="btn-save" @click="save_pass" :disabled="pass_loading">
                {{ pass_loading ? 'שומר...' : 'שמור סיסמה' }}
              </button>
            </div>
          </section>

          <!-- SUBSCRIPTION SECTION -->
          <section v-else-if="activeSection === 'billing'" class="panel-card" aria-label="מנוי וחיוב">
            <div class="panel-card__header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              <h2 class="panel-card__title">מנוי וחיוב</h2>
            </div>

            <div class="billing-plan-card">
              <div class="billing-plan-card__row">
                <span class="billing-plan-card__label">{{ labels.currentPlan }}</span>
                <!-- Legacy/grandfathered: show their real old charge from payment
                     history, not the plan their plan_id was migrated onto. -->
                <span v-if="legacyPlanInfo" class="billing-plan-card__value">
                  {{ legacyPlanInfo.description }}<span class="billing-plan-card__accent"> — ₪{{ legacyPlanInfo.amount }}</span>
                </span>
                <span v-else-if="currentPlanInfo" class="billing-plan-card__value">
                  {{ currentPlanInfo.name }}<span class="billing-plan-card__accent"> — ₪{{ currentPlanInfo.price }}{{ currentPlanInfo.period ? ' ל' + currentPlanInfo.period : '' }}</span>
                </span>
                <!-- User's plan_id may point at a legacy/deactivated package no longer
                     returned by get_plans() (active-only) — don't falsely claim "no
                     subscription" when subscription_status says otherwise. -->
                <span v-else-if="hasActiveSub" class="billing-plan-card__value">מנוי פעיל</span>
                <span v-else class="billing-plan-card__placeholder">אין מנוי פעיל</span>
              </div>
              <div class="billing-plan-card__row">
                <span class="billing-plan-card__label">סוג מנוי</span>
                <span class="billing-plan-card__value">{{ subscriptionTypeLabel }}</span>
              </div>
              <div class="billing-plan-card__row">
                <span class="billing-plan-card__label">{{ labels.paymentMethod }}</span>
                <span v-if="user.last4" class="billing-plan-card__value billing-plan-card__value--mono">****{{ user.last4 }}</span>
                <span v-else-if="user.trz_token" class="billing-plan-card__value">כרטיס שמור</span>
                <span v-else class="billing-plan-card__placeholder">לא רשום</span>
              </div>
              <div v-if="planStartFormatted" class="billing-plan-card__row">
                <span class="billing-plan-card__label">תחילת מנוי</span>
                <span class="billing-plan-card__value">{{ planStartFormatted }}</span>
              </div>
              <div v-if="planExpiryFormatted" class="billing-plan-card__row">
                <span class="billing-plan-card__label">{{ pendingCancel ? 'מנוי פעיל עד' : 'חידוש הבא' }}</span>
                <span class="billing-plan-card__value" :class="{ 'billing-plan-card__value--warn': pendingCancel }">
                  {{ planExpiryFormatted }}
                  <span v-if="pendingCancel" class="billing-plan-card__cancel-note"> — יבוטל בסוף התקופה</span>
                </span>
              </div>
            </div>

            <!-- Payment history — paid rows only, newest first (user/payment_history) -->
            <div class="payment-history">
              <h3 class="payment-history__title">היסטוריית תשלומים</h3>
              <div v-if="paymentsLoading" class="payment-history__state">טוען חיובים...</div>
              <div v-else-if="paymentsError" class="payment-history__state payment-history__state--error">{{ paymentsError }}</div>
              <div v-else-if="payments.length === 0" class="payment-history__state">אין חיובים להצגה</div>
              <ul v-else class="payment-history__list" role="list">
                <li v-for="pay in payments" :key="pay.id" class="payment-history__row">
                  <span class="payment-history__date">{{ formatPaymentDate(pay.date) }}</span>
                  <span class="payment-history__desc">{{ pay.description || (pay.is_refund ? 'זיכוי' : 'חיוב מנוי') }}</span>
                  <span
                    class="payment-history__amount"
                    :class="{ 'payment-history__amount--refund': pay.is_refund }"
                  >{{ pay.is_refund ? '-' : '' }}₪{{ formatPaymentAmount(pay.amount) }}</span>
                </li>
              </ul>
            </div>

            <div class="billing-actions">
              <!-- Card management is offered regardless of subscription state: a
                   lapsed or pending-cancel user still needs to replace an expired
                   card, and previously this button was hidden for them. -->
              <button v-if="hasSavedCard" type="button" class="btn-outline" @click="prepareCardPayment">{{ labels.changeCard }}</button>
              <button v-else type="button" class="btn-outline" @click="prepareCardPayment">{{ labels.addCard }}</button>
              <template v-if="hasActiveSub">
                <!-- Legacy customers keep their old price on renewal, but may opt
                     into one of the two new plans (picker shows paid plans only). -->
                <button type="button" class="btn-outline" @click="show_subscription_plans = !show_subscription_plans; show_payment = false; showPaymentIframe = false">{{ labels.changePlan }}</button>
                <button v-if="pendingCancel" type="button" class="btn-save" @click="resubscribe">הפעלת מנוי מחדש</button>
                <button v-else type="button" class="btn-danger" @click="confirmUnsubscribe">{{ labels.cancelSub }}</button>
              </template>
              <template v-else>
                <button type="button" class="btn-save" @click="show_subscription_plans = !show_subscription_plans; show_payment = false; showPaymentIframe = false">חידוש מנוי</button>
              </template>
            </div>

            <!-- Plan picker (inline, no modal) -->
            <div v-if="show_subscription_plans" class="plan-section">
              <h3 class="plan-section__title">{{ labels.choosePlan }}</h3>
              <div v-if="plansLoading" class="plan-section__state">טוען מסלולים...</div>
              <div v-else-if="plansError" class="plan-section__state plan-section__state--error">{{ plansError }}</div>
              <div class="plan-grid">
                <div
                  v-for="plan in selectablePlans"
                  :key="plan.id"
                  class="plan-card"
                  :class="{ 'plan-card--current': isCurrentPlan(plan) }"
                >
                  <span v-if="isCurrentPlan(plan)" class="plan-card__check" aria-label="המנוי הנוכחי">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  <h4 class="plan-card__name">{{ plan.name }}</h4>
                  <div class="plan-card__price">₪{{ displayPlanPrice(plan) }}</div>
                  <div v-if="plan.period" class="plan-card__period">ל{{ plan.period }}</div>
                  <div v-if="currentPlanPrice > 0 && !isCurrentPlan(plan) && plan.period === currentPlanPeriod" class="plan-card__diff"
                       :class="plan.price > currentPlanPrice ? 'plan-card__diff--up' : 'plan-card__diff--down'">
                    {{ plan.price > currentPlanPrice ? '+' : '' }}₪{{ (plan.price - currentPlanPrice).toFixed(0) }}{{ plan.period ? ' ל' + plan.period : '' }}
                  </div>
                  <button
                    type="button"
                    class="plan-card__cta"
                    :class="isCurrentPlan(plan) ? 'plan-card__cta--current' : 'plan-card__cta--pick'"
                    :disabled="isCurrentPlan(plan) || planPayLoading"
                    @click="changePlan(plan.id)"
                  >{{ isCurrentPlan(plan) ? labels.currentPlanBtn : labels.switchPlan }}</button>
                </div>
              </div>
              <p v-if="planPayError" class="plan-section__state plan-section__state--error">{{ planPayError }}</p>
            </div>

            <!-- Plan change confirm modal (user has saved card) -->
            <div v-if="planChangeModal.show" class="plan-change-modal-backdrop" @click.self="planChangeModal.show = false">
              <div class="plan-change-modal" role="dialog" aria-modal="true">
                <h3 class="plan-change-modal__title">אישור שינוי מנוי</h3>
                <div class="plan-change-modal__body">
                  <div class="plan-change-modal__plan-name">{{ planChangeModal.plan && planChangeModal.plan.name }}</div>
                  <div class="plan-change-modal__price">₪{{ planChangeModal.plan && displayPlanPrice(planChangeModal.plan) }}{{ planChangeModal.plan && planChangeModal.plan.period ? ' ל' + planChangeModal.plan.period : '' }}</div>
                  <div v-if="currentPlanPrice > 0 && planChangeModal.priceDiff !== 0 && planChangeModal.plan && planChangeModal.plan.period === currentPlanPeriod" class="plan-change-modal__diff"
                       :class="planChangeModal.priceDiff > 0 ? 'plan-change-modal__diff--up' : 'plan-change-modal__diff--down'">
                    {{ planChangeModal.priceDiff > 0 ? 'יקר' : 'זול' }} ב-₪{{ Math.abs(planChangeModal.priceDiff).toFixed(0) }}{{ planChangeModal.plan && planChangeModal.plan.period ? ' ל' + planChangeModal.plan.period : '' }} מהמנוי הנוכחי
                  </div>
                  <div class="plan-change-modal__card-note">
                    <span v-if="user.last4">החיוב יבוצע לכרטיס ****{{ user.last4 }}</span>
                    <span v-else>החיוב יבוצע לכרטיס השמור</span>
                  </div>
                </div>
                <p v-if="planChangeModal.error" class="plan-change-modal__error">{{ planChangeModal.error }}</p>
                <div class="plan-change-modal__actions">
                  <button type="button" class="btn-outline" @click="planChangeModal.show = false" :disabled="planChangeModal.loading">ביטול</button>
                  <button type="button" class="btn-save" @click="confirmPlanChange" :disabled="planChangeModal.loading">
                    {{ planChangeModal.loading ? 'מעבד...' : 'אישור חיוב' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Tranzila iframe — plan purchase -->
            <div v-if="showPaymentIframe" class="payment-slot">
              <tranzile :user_id="paymentUserId" :uid="Number(user.id)" :sum="paymentSum" :plan_id="paymentPlanId" @payment_done="onPlanPaymentDone"></tranzile>
            </div>

            <!-- Tranzila iframe — card swap -->
            <div v-if="show_payment" class="payment-slot">
              <tranzile @payment_done="onCardPaymentDone" :user_id="user.user_token" :uid="Number(user.id)"></tranzile>
            </div>
          </section>

          <!-- PRIVACY SECTION -->
          <section v-else-if="activeSection === 'privacy'" class="panel-card" aria-label="פרטיות">
            <div class="panel-card__header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <h2 class="panel-card__title">פרטיות</h2>
            </div>
            <ul class="toggle-list" role="list">
              <li class="toggle-row">
                <span class="toggle-row__label">הורדת הנתונים שלי</span>
                <button type="button" class="btn-outline btn-outline--sm">הורד</button>
              </li>
            </ul>
            <div class="danger-zone">
              <button type="button" class="btn-danger">מחיקת חשבון</button>
              <p class="danger-zone__note">פעולה זו בלתי הפיכה. כל הנתונים ימחקו לצמיתות.</p>
            </div>
          </section>

        </main>

       

      </div><!-- /account-layout -->

    </div><!-- /container -->

    <!-- ── Confirm modal ── -->
    <ui-modal v-model="modal.show" :title="modal.title" size="sm">
      <p class="modal-body-text">{{ modal.body }}</p>
      <template slot="footer">
        <ui-button variant="ghost" size="md" @click="modal.show = false">{{ labels.cancel }}</ui-button>
        <ui-button :variant="modal.confirmVariant" size="md" @click="modal.onConfirm">{{ modal.confirmLabel }}</ui-button>
      </template>
    </ui-modal>

    <!-- ── Alert modal ── -->
    <ui-modal v-model="alert_modal.show" :title="alert_modal.title" size="sm">
      <p class="modal-body-text">{{ alert_modal.body }}</p>
      <template slot="footer">
        <ui-button variant="primary" size="md" @click="alert_modal.show = false">{{ labels.ok }}</ui-button>
      </template>
    </ui-modal>

  </div>
</template>

<script>
import tranzile from '@/components/payment_tranzila.vue';
import UiButton from '@/components/ui/Button.vue';
import UiModal from '@/components/ui/Modal.vue';

// Below this width the nav renders as a wrapped pill grid; above it as a plain
// vertical list.
const SIDEBAR_NAV_BREAKPOINT = 900;

export default {
  components: {
    tranzile,
    UiButton,
    UiModal,
  },
  data() {
    return {
      activeSection: 'profile',
      labels: {
        addCard:       'הוספת אמצעי תשלום',
        cancelSub:      'ביטול מנוי',
        renewSub:       'חידוש מנוי',
        logout:         'יציאה מהחשבון',
        accountOwner:   'בעל החשבון',
        phone:          'טלפון',
        email:          'כתובת מייל',
        password:       'סיסמא',
        newPassword:    'סיסמה חדשה',
        paymentMethod:  'אמצעי תשלום',
        currentPlan:    'סוג מנוי נוכחי',
        editDetails:    'עריכה',
        editPassword:   'שינוי',
        changeCard:     'החלפת כרטיס',
        changePlan:     'שינוי מסלול',
        choosePlan:     'בחר את סוג המנוי הרצוי:',
        currentPlanBtn: 'המנוי הנוכחי',
        switchPlan:     'בחר',
        save:           'שמירה',
        recentViews:    'צפיות אחרונות',
        watch:          'לצפייה',
        cancel:         'ביטול',
        ok:             'אישור',
      },
      last_views: [],
      show_payment: false,
      user: {},
      edit_dets: false,
      edit_pass: false,
      new_password: '',
      confirm_password: '',
      pass_error: '',
      pass_loading: false,
      showNewPass: false,
      showConfirmPass: false,
      show_subscription_plans: false,
      showPaymentIframe: false,
      paymentUserId: '',
      paymentSum: 0,
      paymentPlanId: 0,
      planPayLoading: false,
      planPayError: '',
      subscription_plans: [],
      plansLoading: false,
      plansError: '',
      payments: [],
      paymentsLoading: false,
      paymentsError: '',
      paymentsLoaded: false,
      planChangeModal: {
        show: false,
        plan: null,
        priceDiff: 0,
        loading: false,
        error: '',
      },
      modal: {
        show: false,
        title: '',
        body: '',
        confirmLabel: '',
        confirmVariant: 'primary',
        onConfirm: () => {},
      },
      alert_modal: {
        show: false,
        title: '',
        body: '',
      },
    };
  },
  computed: {
    avatarInitial() {
      const name = this.user.display_name || this.user.username || '';
      return name.charAt(0).toUpperCase() || 'U';
    },
    availablePlans() {
      return Array.isArray(this.subscription_plans) ? this.subscription_plans : [];
    },
    // Plans offered in the change-plan picker: the paid plans only (the two new
    // ones — monthly ₪49 / yearly ₪490). "Free" is never a switch target; you
    // downgrade off a paid plan by cancelling, not by picking a ₪0 plan.
    selectablePlans() {
      return this.availablePlans.filter(p => Number(p.price) > 0);
    },
    currentPlanInfo() {
      const planId = Number(this.user && this.user.plan_id);
      if (!planId) return null;
      return this.availablePlans.find(p => Number(p.id) === planId) || null;
    },
    hasActiveSub() {
      return Number(this.user && this.user.subscription_status) === 1;
    },
    // Grandfathered legacy customer (had a paid charge before the new plans
    // launched). They keep their old price and are never shown the new-plans
    // picker / plan-change option — see billing template gates below.
    isLegacy() {
      return Number(this.user && this.user.is_legacy) === 1;
    },
    // What to render as the legacy customer's "current plan" line: their real
    // last charge (amount + description) reconstructed server-side from payment
    // history, since migration 001 overwrote their plan_id to a new plan.
    legacyPlanInfo() {
      if (!this.isLegacy) return null;
      const amount = Number(this.user && this.user.legacy_amount);
      if (!(amount > 0)) return null;
      return {
        amount,
        description: (this.user && this.user.legacy_description) || 'מנוי',
      };
    },
    pendingCancel() {
      return String(this.user && this.user.cancel_sub) === '1';
    },
    hasSavedCard() {
      return !!(this.user && (this.user.trz_token || this.user.last4));
    },
    currentPlanPrice() {
      if (!this.currentPlanInfo) return 0;
      return Number(this.currentPlanInfo.price) || 0;
    },
    // Billing cadence ("חודש"/"שנה") of the user's current plan. A price
    // difference is only meaningful between plans on the same cadence —
    // ₪490/שנה vs ₪49/חודש isn't a simple "+441", so diff badges are only
    // shown when this matches the candidate plan's period.
    currentPlanPeriod() {
      return (this.currentPlanInfo && this.currentPlanInfo.period) || '';
    },
    subscriptionTypeKey() {
      const fromApi = String((this.user && this.user.subscription_type_label) || '').toLowerCase();
      if (fromApi === 'free' || fromApi === 'monthly' || fromApi === 'yearly') return fromApi;

      if (!this.hasActiveSub) return 'free';
      if (this.currentPlanInfo && this.currentPlanInfo.period === 'yearly') return 'yearly';
      if (this.currentPlanInfo && this.currentPlanInfo.period === 'חודש') return 'monthly';
      if (this.currentPlanInfo && this.currentPlanInfo.period === 'שנה') return 'yearly';
      return 'monthly';
    },
    subscriptionTypeLabel() {
      const map = {
        free: 'חינמי',
        monthly: 'חודשי',
        yearly: 'שנתי',
      };
      return map[this.subscriptionTypeKey] || 'חינמי';
    },
    planStartFormatted() {
      return this.formatDate(this.user && this.user.plan_start_date);
    },
    planExpiryFormatted() {
      return this.formatDate(this.user && this.user.subscription_validity);
    },
  },
  methods: {
    formatDate(val) {
      if (!val) return null;
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    switchSection(section) {
      this.activeSection = section;
      // On mobile, scroll main panel to top after section switch for clarity
      if (typeof window !== 'undefined' && window.innerWidth <= SIDEBAR_NAV_BREAKPOINT) {
        this.$nextTick(() => {
          const mainPanel = this.$el.querySelector('.account-main');
          if (mainPanel) {
            mainPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    _confirm({ title, body, confirmLabel, confirmVariant = 'primary', onConfirm }) {
      this.modal = { show: true, title, body, confirmLabel, confirmVariant, onConfirm: () => {
        this.modal.show = false;
        onConfirm();
      } };
    },
    _alert(body, title = '') {
      this.alert_modal = { show: true, title, body };
    },
    confirmUnsubscribe() {
      this._confirm({
        title: 'ביטול מנוי',
        body: 'האם אתה בטוח שברצונך לבטל את המנוי?',
        confirmLabel: 'כן, בטל מנוי',
        confirmVariant: 'danger',
        onConfirm: this.unsubscribe,
      });
    },
    confirmResubscribe() {
      this._confirm({
        title: 'חידוש מנוי',
        body: 'האם אתה בטוח שברצונך לחדש את המנוי?',
        confirmLabel: 'כן, חדש מנוי',
        confirmVariant: 'primary',
        onConfirm: this.resubscribe,
      });
    },
    confirmLogout() {
      this._confirm({
        title: 'יציאה מהחשבון',
        body: 'האם אתה בטוח שברצונך לצאת מהחשבון?',
        confirmLabel: 'כן, צא',
        confirmVariant: 'danger',
        onConfirm: this.logout,
      });
    },
    loadPaymentHistory(force = false) {
      if (this.paymentsLoading) return;
      if (this.paymentsLoaded && !force) return;
      this.paymentsLoading = true;
      this.paymentsError = '';
      this.api_call({
        url: 'user/payment_history',
        onComplete: (data) => {
          const rows = data && data.data && data.data.data && data.data.data.payments;
          this.payments = Array.isArray(rows) ? rows : [];
          this.paymentsLoading = false;
          this.paymentsLoaded = true;
        },
      }).catch(() => {
        this.payments = [];
        this.paymentsLoading = false;
        this.paymentsError = 'לא ניתן לטעון את היסטוריית התשלומים כרגע';
      });
    },
    formatPaymentDate(raw) {
      if (!raw) return '';
      // MySQL DATETIME ("2026-07-08 16:36:04") — Safari won't parse it with the
      // space, so normalise before handing it to Date.
      const d = new Date(String(raw).replace(' ', 'T'));
      if (isNaN(d.getTime())) return String(raw).split(' ')[0];
      const pad = (n) => (n < 10 ? '0' + n : String(n));
      return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear();
    },
    formatPaymentAmount(amount) {
      const n = Math.abs(Number(amount) || 0);
      return n % 1 === 0 ? String(n) : n.toFixed(2);
    },
    loadSubscriptionPlans() {
      this.plansLoading = true;
      this.plansError = '';
      this.api_call({
        url: 'get_plans',
        onComplete: (data) => {
          const raw = data && data.data && data.data.data;
          const plans = this.normalizePaymentPlans(raw);
          if (plans.length > 0) {
            this.subscription_plans = plans;
            this.plansLoading = false;
            return;
          }
          this.loadPlansFromGeneral();
        },
      }).catch(() => {
        this.loadPlansFromGeneral();
      });
    },
    loadPlansFromGeneral() {
      const fromStore = this.normalizeRestrictionPlans(this.$store && this.$store.state && this.$store.state.restrictions_plans);
      if (fromStore.length > 0) {
        this.subscription_plans = fromStore;
        this.plansLoading = false;
        this.plansError = '';
        return;
      }

      this.api_call({
        url: 'general',
        data: { is_profile_valid: false },
        onComplete: (data) => {
          const restrictions = data && data.data && data.data.data && data.data.data.restrictions_plans;
          const plans = this.normalizeRestrictionPlans(restrictions);
          this.subscription_plans = plans;
          this.plansLoading = false;
          this.plansError = plans.length > 0 ? '' : 'לא נמצאו מסלולים זמינים כרגע';
        },
      }).catch(() => {
        this.subscription_plans = [];
        this.plansLoading = false;
        this.plansError = 'לא ניתן לטעון מסלולים כרגע';
      });
    },
    normalizePaymentPlans(raw) {
      let list = [];
      if (Array.isArray(raw)) list = raw;
      else if (raw && Array.isArray(raw.items)) list = raw.items;
      else if (raw && Array.isArray(raw.plans)) list = raw.plans;
      else if (raw && typeof raw === 'object') list = Object.values(raw);

      return list
        .filter((p) => p && Number(p.id) > 0)
        .map((p) => {
          const price = Number(p.discount_months) > 0 ? Number(p.discount_price || p.price || 0) : Number(p.price || 0);
          return {
            id: Number(p.id),
            name: p.name || 'מסלול',
            price,
            // A free plan has no billing cadence to show, regardless of what
            // `period` the DB row happens to carry (ENUM defaults to 'monthly').
            period: price > 0 ? this.periodNoun(p.period) : '',
          };
        });
    },
    // Maps the API's `period` ("monthly" | "yearly") to the Hebrew noun used
    // in labels like "ל{{ period }}" ("לחודש" / "לשנה"). Unknown/missing
    // period (e.g. the free plan) renders no billing-interval label.
    periodNoun(period) {
      if (period === 'yearly') return 'שנה';
      if (period === 'monthly') return 'חודש';
      return '';
    },
    normalizeRestrictionPlans(rows) {
      if (!Array.isArray(rows)) return [];
      return rows
        .filter((r) => r && Number(r.id) > 0)
        .map((r) => ({
          id: Number(r.id),
          name: r.name || 'מסלול',
          price: Number(r.price || 0),
          period: Number(r.subscription_days || 30) >= 365 ? 'שנה' : 'חודש',
        }));
    },
    isCurrentPlan(plan) {
      const userPlanId = Number(this.user && this.user.plan_id);
      if (userPlanId > 0) return Number(plan.id) === userPlanId;
      return Number(plan.price) === Number(this.user && this.user.subscription_price);
    },
    displayPlanPrice(plan) {
      const price = Number(plan && plan.price);
      return Number.isFinite(price) ? price : 0;
    },
    changePlan(planId) {
      const plan = this.availablePlans.find(p => p.id === planId);
      if (!plan) return;

      if (this.hasSavedCard) {
        const priceDiff = Number((plan.price - this.currentPlanPrice).toFixed(2));
        this.planChangeModal = { show: true, plan, priceDiff, loading: false, error: '' };
      } else {
        // No card — create_payment now runs inside <payment_tranzila> on mount; just hand it the plan.
        this.planPayError = '';
        this.paymentSum = plan.price;
        this.paymentPlanId = planId;
        this.show_subscription_plans = false;
        this.showPaymentIframe = true;
      }
    },
    confirmPlanChange() {
      const { plan } = this.planChangeModal;
      if (!plan) return;
      this.planChangeModal.loading = true;
      this.planChangeModal.error = '';
      this.api_call({
        url: 'charge_plan',
        data: { plan_id: plan.id },
        onComplete: (resp) => {
          this.planChangeModal.loading = false;
          const d = resp && resp.data && resp.data.data;
          if (d && d.success) {
            this.planChangeModal.show = false;
            this.show_subscription_plans = false;
            this._alert('המנוי עודכן בהצלחה!');
            this.api_call({
              url: 'general',
              onComplete: (data) => { this.user = data.data.data.user; },
            });
            this.loadPaymentHistory(true);
          } else {
            this.planChangeModal.error = d && d.msg ? d.msg : 'החיוב נכשל. נסה/י שוב.';
          }
        },
      }).catch(() => {
        this.planChangeModal.loading = false;
        this.planChangeModal.error = 'שגיאת חיבור. נסה/י שוב.';
      });
    },
    onPlanPaymentDone(success = true) {
      this.showPaymentIframe = false;
      if (!success) {
        this.planPayError = 'התשלום לא הושלם. ניתן לבחור חבילה אחרת או לנסות שוב.';
        this.show_subscription_plans = true;
        return;
      }
      this._alert('הפרטים עודכנו בהצלחה! המנוי שלך פעיל.');
      this.api_call({
        url: 'general',
        onComplete: (data) => { this.user = data.data.data.user; },
      });
      this.loadPaymentHistory(true);
    },
    prepareCardPayment() {
      this.show_subscription_plans = false;
      this.showPaymentIframe = false;
      // Reset token_change on server so check_payment won't fire from stale data
      this.api_call({
        url: 'pay',
        data: { plan_id: 0 },
        onComplete: () => {
          this.show_payment = true;
        },
      }).catch(() => {
        // Reset failed — show iframe anyway, worst case is a stale false positive
        this.show_payment = true;
      });
    },
    onCardPaymentDone(success = true) {
      this.show_payment = false;
      if (!success) {
        this._alert('עדכון הכרטיס לא הושלם. נסה/י שוב.');
        return;
      }
      this.api_call({
        url: 'general',
        onComplete: (data) => {
          this.user = data.data.data.user;
          this._alert('פרטי כרטיס האשראי עודכנו בהצלחה!');
        },
      });
    },
    logout() {
      localStorage.removeItem('user_token');
      localStorage.removeItem('user_restrictions');
      Object.keys(localStorage)
        .filter(k => k.startsWith('preview_used_') || k.startsWith('article_credit_'))
        .forEach(k => localStorage.removeItem(k));
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('preview_used_'))
        .forEach(k => sessionStorage.removeItem(k));
      document.location = '/';
    },
    save_details() {
      if (!this.user.display_name) { this._alert('יש למלא את כל הפרטים'); return; }
      this.edit_dets = false;
      this.api_call({
        url: 'update_user_details',
        data: { display_name: this.user.display_name, phone: this.user.phone },
        onComplete: () => { this._alert('הפרטים עודכנו בהצלחה!'); },
      });
    },
    save_pass() {
      this.pass_error = '';
      if (!this.new_password || this.new_password.length < 6) { this.pass_error = 'סיסמה חדשה חייבת להכיל לפחות 6 תווים'; return; }
      if (this.new_password !== this.confirm_password) { this.pass_error = 'הסיסמאות אינן תואמות'; return; }
      this.pass_loading = true;
      this.api_call({
        url: 'update_user_details',
        data: { password: this.new_password },
        onComplete: () => {
          this.pass_loading = false;
          this.new_password = '';
          this.confirm_password = '';
          this.showNewPass = false;
          this.showConfirmPass = false;
          this._alert('הסיסמה עודכנה בהצלחה!');
        },
      }).catch(() => {
        this.pass_loading = false;
        this.pass_error = 'שגיאת חיבור. נסה/י שוב.';
      });
    },
    unsubscribe() {
      const self = this;
      this.api_call({
        url: 'user/cancel_subscription',
        onComplete() {
          self._alert('המנוי בוטל');
          self.api_call({
            url: 'general',
            onComplete(data) { self.user = data.data.data.user; },
          });
        },
      });
    },
    resubscribe() {
      const self = this;
      this.api_call({
        url: 'user/renew_subscription',
        onComplete() {
          self._alert('המנוי חודש');
          self.api_call({
            url: 'general',
            onComplete(data) { self.user = data.data.data.user; },
          });
        },
      });
    },
  },
  watch: {
    // Billing is not the default section — fetch the history the first time it
    // is actually opened rather than on every account-page load.
    activeSection(section) {
      if (section === 'billing') this.loadPaymentHistory();
    },
  },
  created() {
    const self = this;
    const routeSection = this.$route && this.$route.query && this.$route.query.section;
    const allowedSections = ['profile', 'billing', 'password', 'privacy', 'preferences', 'notifications'];
    if (allowedSections.indexOf(routeSection) !== -1) {
      this.activeSection = routeSection;
    }
    this.api_call({
      url: 'items/results',
      data: { user_history: true },
      onComplete(data) { self.last_views = data.data.data.items; },
    });
    this.api_call({
      url: 'general',
      onComplete(data) { self.user = data.data.data.user; },
    });
  },
  mounted() {
    this.loadSubscriptionPlans();
    // Deep link straight into ?section=billing sets activeSection in created(),
    // before the watcher exists — cover that case here.
    if (this.activeSection === 'billing') this.loadPaymentHistory();
  },
};
</script>

<style scoped lang="scss">
/* ── Page shell ── */
.user-page {
  background: var(--color-bg);
  min-height: 100vh;
  font-family: var(--font-hebrew);
  color: var(--color-text);
  /* Never let inner content (long emails, autoWidth pills, plan cards) push the
     page wider than the viewport / cause a horizontal scrollbar on mobile. */
  overflow-x: hidden;
  max-width: 100%;
}

/* ── Container ── */
.user-page__container {
  max-width: 1280px;
  margin-inline: auto;
  padding-inline: var(--space-8);
  padding-block: var(--space-12);
}

/* ── Page H1 ── */
.page-title {
  margin: 0 0 var(--space-8) 0;
  font-family: var(--font-sans);
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  line-height: 1.15;
  color: var(--color-text);
}

/* ── Two-column layout ──
   Column order matches DOM order (aside first): col1 = sidebar (25%, inline-start,
   right side in RTL), col2 = main (1fr, inline-end, left side in RTL). Area names
   are assigned explicitly below so placement no longer depends on auto-placement /
   DOM order accidentally matching the template-columns order. */
.account-layout {
  display: grid;
  grid-template-columns: 25% 1fr;
  grid-template-areas: "sidebar main";
  gap: var(--space-8);
  align-items: start;
  /* Grid itself must not exceed its container, else the whole page scrolls x. */
  max-width: 100%;
  min-width: 0;
}

.account-sidebar {
  grid-area: sidebar;
  /* Nav pills (autoWidth / max-content) must not stretch the sidebar track. */
  min-width: 0;
}

.account-main {
  grid-area: main;
  /* Prevent long/unbreakable content (inputs, plan cards) from stretching the
     1fr track beyond the viewport — grid items default to min-width:auto. */
  min-width: 0;
}

/* ── Panel card (main content) ── */
.panel-card {
  background: var(--color-surface1);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.panel-card__header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-accent);
}

.panel-card__title {
  margin: 0;
  font-family: var(--font-hebrew);
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.3;
}

/* ── Form fields ── */
.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-family: var(--font-hebrew);
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: var(--color-textMuted);
  
}

.form-input {
  width: 100%;
  height: 50px;
  padding-inline: var(--space-4);
  padding-block: var(--space-3);
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-hebrew);
  font-size: var(--text-base);
  color: var(--color-text);
  direction: rtl;
  box-sizing: border-box;
  transition: border-color 200ms ease, box-shadow 200ms ease;
}
iframe:not(.md-image) {
  height: 60vh !important;
}
.form-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(225, 29, 42, 0.18);
}

.form-input--disabled,
.form-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Panel footer (save button) ── */
.panel-card__footer {
  display: flex;
  justify-content: center;
  padding-top: var(--space-8);
  border-block-start: 1px solid var(--color-border);
}

.btn-save {
  padding-inline: var(--space-6);
  padding-block: var(--space-3);
  background: var(--color-accent);
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-hebrew);
  font-size: var(--text-base);
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
  min-height: 44px;
  min-width: 30%;
  transition: opacity 200ms ease;
}

.btn-save:hover { opacity: 0.88; }
.btn-save:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 3px;
}

.btn-outline {
  padding-inline: var(--space-4);
  padding-block: var(--space-2);
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-hebrew);
  font-size: var(--text-sm);
  color: var(--color-text);
  cursor: pointer;
  min-height: 44px;
  transition: border-color 200ms ease, background 200ms ease;
}

.btn-outline:hover {
  border-color: var(--color-borderHi);
  background: var(--color-surface2);
}

.btn-outline:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 3px;
}

.btn-outline--sm { min-height: 36px; }

.btn-danger {
  padding-inline: var(--space-4);
  padding-block: var(--space-2);
  background: transparent;
  border: 1px solid var(--color-error, #e11d2a);
  border-radius: var(--radius-md);
  font-family: var(--font-hebrew);
  font-size: var(--text-sm);
  color: var(--color-error, #e11d2a);
  cursor: pointer;
  min-height: 44px;
  transition: background 200ms ease;
}

.btn-danger:hover { background: rgba(225, 29, 42, 0.08); }
.btn-danger:focus-visible {
  outline: 2px solid var(--color-error, #e11d2a);
  outline-offset: 3px;
}

/* ── Billing section ── */
.billing-plan-card {
  padding:20px;
  border-radius: var(--radius-md);
  overflow: hidden;
}

.billing-plan-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 4px;
  gap: var(--space-4);
  padding: var(--space-3) 0;
  border-block-end: 1px solid var(--color-border);
}

.billing-plan-card__row:last-child { border-block-end: none; }

.billing-plan-card__row--muted { opacity: 0.6; }

.billing-plan-card__label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-textMuted);
  flex-shrink: 0;
}

.billing-plan-card__value {
  font-size: var(--text-base);
  color: var(--color-text);
  min-width: 0;
  overflow-wrap: break-word;
}

.billing-plan-card__value--mono { font-family: var(--font-mono, monospace); }
.billing-plan-card__value--warn { color: var(--color-warning, #e67e22); }
.billing-plan-card__cancel-note { font-size: var(--text-sm); opacity: 0.85; }

.billing-plan-card__accent { color: var(--color-accent); }

.billing-plan-card__placeholder {
  font-size: var(--text-sm);
  color: var(--color-textDim);
  font-style: italic;
}

.billing-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

/* ── Payment history ── */
.payment-history {
  padding: 0 20px;
}

.payment-history__title {
  margin: 0 0 var(--space-3) 0;
  font-family: var(--font-hebrew);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-textMuted);
}

.payment-history__state {
  font-size: var(--text-sm);
  color: var(--color-textDim);
}

.payment-history__state--error {
  color: var(--color-error, #e11d2a);
}

.payment-history__list {
  list-style: none;
  margin: 0;
  padding: 0;
  /* Long histories scroll inside the card instead of pushing the billing
     actions off-screen. */
  max-height: 320px;
  overflow-y: auto;
}

.payment-history__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-3) 0;
  border-block-end: 1px solid var(--color-border);
}

.payment-history__row:last-child { border-block-end: none; }

.payment-history__date {
  font-size: var(--text-sm);
  color: var(--color-textMuted);
  font-family: var(--font-mono, monospace);
  flex-shrink: 0;
}

.payment-history__desc {
  font-size: var(--text-sm);
  color: var(--color-text);
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-history__amount {
  font-size: var(--text-base);
  color: var(--color-text);
  font-family: var(--font-mono, monospace);
  flex-shrink: 0;
}

.payment-history__amount--refund {
  color: var(--color-success, #27ae60);
}

/* ── Plan section ── */
.plan-section {
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-6);
}

.plan-section__state {
  text-align: center;
  color: var(--color-textMuted);
  margin-block-end: var(--space-4);
}

.plan-section__state--error {
  color: var(--color-error, #e11d2a);
}

.plan-section__title {
  margin: 0 0 var(--space-6) 0;
  font-family: var(--font-hebrew);
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--color-text);
  text-align: center;
}

.plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--space-4);
}

.plan-card {
  position: relative;
  background: var(--color-surface3, var(--color-surface2));
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  padding: var(--space-6) var(--space-4);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  transition: transform 200ms ease, box-shadow 200ms ease;
}

.plan-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.plan-card--current {
  border-color: var(--color-accent);
}

.plan-card__check {
  position: absolute;
  inset-block-start: var(--space-2);
  inset-inline-start: var(--space-3);
  color: var(--color-accent);
}

.plan-card__name {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text);
}

.plan-card__price {
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--color-accent);
  font-family: var(--font-mono, monospace);
  line-height: 1.1;
}

.plan-card__period {
  font-size: var(--text-sm);
  color: var(--color-textMuted);
  margin-block-end: var(--space-2);
}

.plan-card__cta {
  width: 100%;
  padding-block: var(--space-2);
  border-radius: var(--radius-md);
  font-family: var(--font-hebrew);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  transition: opacity 200ms ease;
  border: none;
}

.plan-card__cta--pick {
  background: var(--color-accent);
  color: #ffffff;
}

.plan-card__cta--pick:hover { opacity: 0.88; }

.plan-card__cta--current {
  background: var(--color-surface2);
  color: var(--color-textMuted);
  cursor: not-allowed;
  opacity: 0.7;
}

.plan-card__cta:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 3px;
}

/* Plan price diff badge */
.plan-card__diff {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 99px;
  margin-bottom: var(--space-2);
  display: inline-block;
}
.plan-card__diff--up   { background: rgba(231,76,60,0.12); color: #c0392b; }
.plan-card__diff--down { background: rgba(39,174,96,0.12);  color: #27ae60; }

/* Plan change confirm modal */
.plan-change-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}
.plan-change-modal {
  background: black;
  border: 1px solid var(--color-border);
  box-shadow: #e11d2a 0px 4px 12px;
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  max-width: 380px;
   width: 90%;
  display: flex;
   flex-direction: column; 
   gap: var(--space-4);
  direction: rtl;
}
.plan-change-modal__title  { font-size: var(--text-lg); font-weight: 700; margin: 0; }
.plan-change-modal__plan-name { font-size: var(--text-base); font-weight: 600; }
.plan-change-modal__price  { font-size: 22px; font-weight: 700; color: var(--color-accent); }
.plan-change-modal__diff   { font-size: 13px; font-weight: 600; padding: 3px 10px; border-radius: 99px; display: inline-block; }
.plan-change-modal__diff--up   { background: rgba(231,76,60,0.12); color: #c0392b; }
.plan-change-modal__diff--down { background: rgba(39,174,96,0.12);  color: #27ae60; }
.plan-change-modal__card-note { font-size: 13px; color: var(--color-textMuted); }
.plan-change-modal__error  { font-size: 13px; color: var(--color-error, #e74c3c); margin: 0; }
.plan-change-modal__actions { display: flex; gap: var(--space-3); justify-content: flex-end; }

/* ── Payment slot ── */
.payment-slot {
  padding-block-start: var(--space-6);
  min-height: 72vh;
  border-block-start: 1px solid var(--color-border);
}

.payment-slot :deep(.tranzila-wrap) {
  width: 100%;
  min-height: 72vh;
}

.payment-slot :deep(#tranzila-frame) {
  width: 100% !important;
  height: 72vh !important;
  min-height: 740px !important;
}

@media (max-width: 900px) {
  .payment-slot {
    min-height: 72vh;
  }

  .payment-slot :deep(.tranzila-wrap),
  .payment-slot :deep(#tranzila-frame) {
    min-height: 520px !important;
    height: 72vh !important;
  }
}
/* ── Toggle list (preferences / notifications / privacy) ── */
.toggle-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: var(--space-4);
  border-block-end: 1px solid var(--color-border);
  gap: var(--space-4);
}

.toggle-row:last-child { border-block-end: none; }

.toggle-row__label {
  font-family: var(--font-hebrew);
  font-size: var(--text-base);
  color: var(--color-text);
}

.toggle-row__badge {
  font-size: 0.75rem;
  color: var(--color-textDim);
  font-style: italic;
}

/* ── Danger zone ── */
.danger-zone {
  
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: flex-start;
}

.danger-zone__note {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-textMuted);
  line-height: 1.5;
  padding:5px 7px;
}

/* ── Sidebar card ── */
.sidebar-card {
  background: var(--color-surface1);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  gap: 0;
  position: sticky;
  top: var(--space-8);
}

/* ── User mini card ── */
.user-mini-card {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-block-end: var(--space-6);
}

.user-mini-card__avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--color-accent);
  color: #ffffff;
  font-family: var(--font-sans);
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-mini-card__info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  
}

.user-mini-card__name {
  font-family: var(--font-hebrew);
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-mini-card__email {
  font-family: var(--font-hebrew);
  font-size: 0.8125rem;
  color: var(--color-textMuted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-mini-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-accent);
  line-height: 1;
  margin-block-start: 2px;
  margin-right: auto;
}

.user-mini-card__badge--free {
  background: rgba(148, 163, 184, 0.25);
  color: #d8e0ea;
}

.user-mini-card__badge--monthly {
  background: rgba(59, 130, 246, 0.2);
  color: #bfdbfe;
}

.user-mini-card__badge--yearly {
  background: rgba(245, 158, 11, 0.24);
  color: #fcd34d;
}

/* ── Sidebar divider ── */
.sidebar-divider {
  height: 1px;
  background: var(--color-border);
  margin-block-end: var(--space-3);
}

/* ── Sidebar nav wrapper ── */
.sidebar-nav-wrap {
  min-width: 0;
}

/* ── Sidebar nav ── */
.sidebar-nav {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  height: auto;
}

.sidebar-nav__item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--space-3);
  width: 100%;
  min-height: 44px;
  padding-inline: var(--space-4);
  padding-block: var(--space-3);
  background: transparent;
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  font-family: var(--font-hebrew);
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text);
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;
}

.sidebar-nav__item svg {
  flex-shrink: 0;
  order: 0;
}

.sidebar-nav__item:hover {
  background: var(--color-surface3, var(--color-surface2));
}

.sidebar-nav__item:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--color-accent);
}

.sidebar-nav__item--active {
  background: var(--color-accent);
  color: #ffffff;
  font-weight: 700;
  box-shadow: inset 0 0 0 2px #ffffff;
}

.sidebar-nav__item--active:hover {
  background: var(--color-accent);
  opacity: 0.92;
}

/* Logout item */
.sidebar-nav__item--logout {
  margin-block-start: var(--space-3);
  color: var(--color-textMuted);
  border-block-start: 1px solid var(--color-border);
  border-radius: 0;
  padding-block-start: var(--space-4);
  justify-content: space-between;
}

.sidebar-nav__item--logout:hover {
  color: var(--color-error, #e11d2a);
  background: transparent;
}

.sidebar-nav__logout-icon {
  flex-shrink: 0;
  order: 0;
}

/* ── Password input with eye toggle ── */
.pass-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.pass-input {
  padding-inline-start: var(--space-10, 40px);
}

.pass-eye {
  position: absolute;
  inset-inline-end: var(--space-3);
  inset-block: 0;
  margin-block: auto;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--color-textMuted);
  display: flex;
  align-items: center;
  transition: color 150ms ease;
  height: 32px;
}

.pass-eye:hover { color: var(--color-text); }
.pass-eye:focus-visible {
  outline: 2px solid var(--color-accent);
  border-radius: 4px;
}

/* ── Password error ── */
.pass-error {
  font-size: var(--text-sm);
  color: var(--color-error, #e11d2a);
  padding: var(--space-2) 0;
}

/* ── Modal body ── */
.modal-body-text {
  margin: 0;
  font-size: var(--text-base);
  line-height: 1.5;
  color: var(--color-textMuted);
  font-family: var(--font-hebrew);
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .form-input,
  .btn-save,
  .btn-outline,
  .btn-danger,
  .plan-card,
  .plan-card__cta,
  .sidebar-nav__item {
    transition: none;
  }
}

/* ── Mobile (<900px): stack sidebar above main ── */
@media (max-width: 900px) {
  .user-page__container {
    padding-inline: var(--space-4);
    padding-block: var(--space-8);
  }

  .page-title {
    font-size: 2rem;
  }

  .account-layout {
    grid-template-columns: 1fr;
    grid-template-areas:
      "sidebar"
      "main";
  }

  .sidebar-card {
    position: static;
    padding: var(--space-4);
  }

  .sidebar-divider,
  .user-mini-card {
    display: none;
  }

  /* Main content card: trim desktop padding across the whole mobile range so
     the stacked single-column layout uses the narrow width efficiently (the
     <=480 block tightens it further). */
  .panel-card {
    padding: var(--space-5);
    gap: var(--space-5);
  }

  .plan-section {
    padding: var(--space-4);
  }

  .billing-plan-card {
    padding: var(--space-4);
  }

  /* Wrapped pill row — every section visible at once. This was a one-row
     swiper, so anything past the second pill needed a drag to reach. */
  .sidebar-nav {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  .sidebar-nav__item {
    width: auto;
    white-space: nowrap;
    padding-inline: var(--space-4);
    padding-block: var(--space-2);
    border-radius: 999px;
    font-size: 0.74rem;
  }

  .sidebar-nav__item--logout {
    margin-block-start: 0;
    border-block-start: none;
    padding-block-start: var(--space-2);
  }

  .plan-grid {
    grid-template-columns: 1fr 1fr;
  }

  /* iOS Safari zooms the viewport on focus for inputs with font-size <16px —
     keep text-input fields at 16px on touch/mobile widths. */
  .form-input {
    font-size: 16px;
  }
}

/* ── Narrow-width audit (<480px, iPhone SE ~375px) ──
   Tighter padding/type scale so panel content, billing rows and plan cards
   never force horizontal scroll on small phones. RTL-safe: only logical
   (inline-start/end) spacing and flex layout are touched, no left/right
   positioning. */
@media (max-width: 480px) {
  .user-page__container {
    padding-inline: var(--space-3);
  }

  .panel-card {
    padding: var(--space-4);
    gap: var(--space-4);
  }

  .panel-card__title {
    font-size: 1.125rem;
  }

  .billing-plan-card {
    padding: var(--space-3);
  }

  .billing-plan-card__row {
    padding: var(--space-2) 0;
  }

  .plan-grid {
    grid-template-columns: 1fr;
  }

  .plan-card {
    padding: var(--space-4) var(--space-3);
  }

  .billing-actions {
    flex-direction: column;
  }

  /* Full-width buttons when stacked — avoids content-sized buttons of very
     different widths and keeps tap targets consistent on narrow screens. */
  .billing-actions .btn-outline,
  .billing-actions .btn-save,
  .billing-actions .btn-danger {
    width: 100%;
  }

  .panel-card__footer .btn-save {
    width: 100%;
  }
}
</style>
